import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Upload, CheckCircle, AlertCircle, XCircle, Camera } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

import { BACKEND_URL, API } from '../config';

const toBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const KYC = () => {
  const { user, checkAuth } = useAuth();
  const [step, setStep] = useState(1);
  const [adminMsg, setAdminMsg] = useState(null);
  const [showResubmitForm, setShowResubmitForm] = useState(false);
  const [formData, setFormData] = useState({
    document_type: 'id_card',
    document_number: '',
    country: '',
    phone: '',
    document_front: null,
    document_back: null,
    selfie: null,
  });
  const [previews, setPreviews] = useState({ document_front: null, document_back: null, selfie: null });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.kyc_status === 'submitted' || user?.kyc_status === 'rejected') {
      axios.get(`${API}/kyc/status`, { withCredentials: true })
        .then(r => setAdminMsg(r.data.admin_message))
        .catch(() => {});
    }
  }, [user?.kyc_status]);

  const handleFileChange = (field, file) => {
    if (!file) return;
    setFormData(prev => ({...prev, [field]: file}));
    const reader = new FileReader();
    reader.onloadend = () => setPreviews(prev => ({...prev, [field]: reader.result}));
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const body = {
        document_type: formData.document_type,
        document_number: formData.document_number,
        country: formData.country,
        phone: formData.phone,
        document_front_b64: await toBase64(formData.document_front),
        document_back_b64: await toBase64(formData.document_back),
        selfie_b64: await toBase64(formData.selfie),
      };
      await axios.post(`${API}/kyc/submit`, body, { withCredentials: true });
      await checkAuth();
      toast.success('KYC enviado correctamente. En revisión.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar KYC');
    } finally {
      setLoading(false);
    }
  };

  if (user?.kyc_status === 'verified') {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto" data-testid="kyc-verified">
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader>
              <div className="flex items-center gap-3">
                <CheckCircle className="text-positive" size={32} />
                <div>
                  <CardTitle className="text-white">Identidad Verificada</CardTitle>
                  <CardDescription>Tu identidad ha sido verificada exitosamente. Ya puedes usar la plataforma.</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>
        </div>
      </Layout>
    );
  }

  if (user?.kyc_status === 'submitted') {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto" data-testid="kyc-pending">
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader>
              <div className="flex items-center gap-3">
                <AlertCircle className="text-[#FFD600]" size={32} />
                <div>
                  <CardTitle className="text-white">KYC En Revisión</CardTitle>
                  <CardDescription>Tus documentos están siendo verificados. Esto toma entre 1 y 48 horas.</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>
        </div>
      </Layout>
    );
  }

  if (user?.kyc_status === 'rejected' && !showResubmitForm) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto space-y-4" data-testid="kyc-rejected">
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader>
              <div className="flex items-center gap-3">
                <XCircle className="text-red-500" size={32} />
                <div>
                  <CardTitle className="text-white">KYC Rechazado</CardTitle>
                  <CardDescription>Tu verificación fue rechazada. Revisa el mensaje del administrador y vuelve a enviar tus documentos.</CardDescription>
                </div>
              </div>
            </CardHeader>
            {adminMsg && (
              <CardContent>
                <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
                  <p className="text-sm font-medium text-red-400 mb-1">Mensaje del administrador:</p>
                  <p className="text-sm text-white">{adminMsg}</p>
                </div>
              </CardContent>
            )}
          </Card>
          <Button onClick={async () => {
            try {
              await axios.post(`${API}/kyc/reset`, {}, { withCredentials: true });
              await checkAuth();
            } catch (e) {}
            setStep(1);
            setFormData({ document_type: 'id_card', document_number: '', country: '', phone: '', document_front: null, document_back: null, selfie: null });
            setPreviews({ document_front: null, document_back: null, selfie: null });
            setShowResubmitForm(true);
          }} className="w-full bg-[#00D4FF] text-black">
            Volver a enviar documentos
          </Button>
        </div>
      </Layout>
    );
  }

  // ========= FORMULARIO KYC =========
  return (
    <Layout>
      <div className="max-w-3xl mx-auto" data-testid="kyc-form">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white tracking-tight">Verificación de Identidad (KYC)</h1>
          <p className="text-[#A1A1AA] mt-1">Completa la verificación para desbloquear todas las funciones</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-between mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center flex-1">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                step >= s ? 'bg-[#00D4FF] text-black' : 'bg-[#1E1E1E] text-[#52525B]'
              }`}>{s}</div>
              {s < 3 && <div className={`flex-1 h-1 mx-2 ${step > s ? 'bg-[#00D4FF]' : 'bg-[#1E1E1E]'}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Datos personales */}
        {step === 1 && (
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader>
              <CardTitle className="text-white">Datos Personales</CardTitle>
              <CardDescription>Tu correo ya está registrado. Completa los demás datos.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Correo electrónico</Label>
                <Input value={user?.email || ''} disabled className="opacity-60" />
              </div>
              <div>
                <Label>País</Label>
                <Input value={formData.country} onChange={e => setFormData({...formData, country: e.target.value})} placeholder="Ej: Colombia, México, España..." />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} placeholder="+57 300 123 4567" />
              </div>
              <div>
                <Label>Tipo de documento</Label>
                <Select value={formData.document_type} onValueChange={v => setFormData({...formData, document_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="id_card">Cédula / ID Nacional</SelectItem>
                    <SelectItem value="passport">Pasaporte</SelectItem>
                    <SelectItem value="drivers_license">Licencia de conducir</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Número de documento</Label>
                <Input value={formData.document_number} onChange={e => setFormData({...formData, document_number: e.target.value})} placeholder="Número de tu documento" />
              </div>
              <Button onClick={() => setStep(2)} disabled={!formData.country || !formData.phone || !formData.document_number} className="w-full bg-[#00D4FF] text-black">
                Siguiente
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Fotos del documento */}
        {step === 2 && (
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader>
              <CardTitle className="text-white">Fotos del Documento</CardTitle>
              <CardDescription>Sube fotos claras de tu documento por el frente y por detrás</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Front */}
              <div>
                <Label>Documento — Frente</Label>
                <label htmlFor="doc-front" className="mt-2 border-2 border-dashed border-[#27272A] rounded p-4 text-center hover:border-[#00D4FF] transition-colors cursor-pointer block">
                  {previews.document_front ? (
                    <img src={previews.document_front} alt="Frente" className="max-h-48 mx-auto rounded" />
                  ) : (
                    <div className="py-4"><Upload className="mx-auto text-[#A1A1AA] mb-2" size={36} /><p className="text-[#A1A1AA] text-sm">Foto frontal del documento</p></div>
                  )}
                  <input type="file" accept="image/*" onChange={e => handleFileChange('document_front', e.target.files[0])} className="hidden" id="doc-front" />
                  <div className="mt-2 w-full py-2.5 text-center rounded-lg bg-[#e8f5f0] border border-[#c5e4d9] text-sm font-medium text-[#2d7a5f] hover:bg-[#d4ede3] active:scale-[0.97] active:bg-[#c0e5d5] transition-all">{previews.document_front ? 'Cambiar foto' : 'Subir foto'}</div>
                </label>
              </div>
              {/* Back */}
              <div>
                <Label>Documento — Reverso</Label>
                <label htmlFor="doc-back" className="mt-2 border-2 border-dashed border-[#27272A] rounded p-4 text-center hover:border-[#00D4FF] transition-colors cursor-pointer block">
                  {previews.document_back ? (
                    <img src={previews.document_back} alt="Reverso" className="max-h-48 mx-auto rounded" />
                  ) : (
                    <div className="py-4"><Upload className="mx-auto text-[#A1A1AA] mb-2" size={36} /><p className="text-[#A1A1AA] text-sm">Foto del reverso del documento</p></div>
                  )}
                  <input type="file" accept="image/*" onChange={e => handleFileChange('document_back', e.target.files[0])} className="hidden" id="doc-back" />
                  <div className="mt-2 w-full py-2.5 text-center rounded-lg bg-[#e8f5f0] border border-[#c5e4d9] text-sm font-medium text-[#2d7a5f] hover:bg-[#d4ede3] active:scale-[0.97] active:bg-[#c0e5d5] transition-all">{previews.document_back ? 'Cambiar foto' : 'Subir foto'}</div>
                </label>
              </div>
              <div className="flex gap-3">
                <Button onClick={() => setStep(1)} variant="outline" className="flex-1">Atrás</Button>
                <Button onClick={() => setStep(3)} disabled={!formData.document_front || !formData.document_back} className="flex-1 bg-[#00D4FF] text-black">Siguiente</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Selfie con documento */}
        {step === 3 && (
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader>
              <CardTitle className="text-white">Selfie con Documento</CardTitle>
              <CardDescription>Tómate una foto sosteniendo tu documento a la altura de la barbilla. Debe verse tu cara y el documento claramente.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-[#1E1E1E] border border-[#27272A] rounded p-4 flex items-start gap-3">
                <Camera className="text-[#00D4FF] mt-0.5 shrink-0" size={20} />
                <div className="text-sm text-[#A1A1AA]">
                  <p className="font-medium text-white mb-1">Instrucciones:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Sostén el documento junto a tu barbilla</li>
                    <li>Tu rostro debe ser visible completamente</li>
                    <li>El documento debe ser legible en la foto</li>
                    <li>Buena iluminación, sin reflejos ni sombras</li>
                  </ul>
                </div>
              </div>
              <div>
                <label htmlFor="selfie-input" className="border-2 border-dashed border-[#27272A] rounded p-4 text-center hover:border-[#00D4FF] transition-colors cursor-pointer block">
                  {previews.selfie ? (
                    <img src={previews.selfie} alt="Selfie" className="max-h-56 mx-auto rounded" />
                  ) : (
                    <div className="py-6"><Camera className="mx-auto text-[#A1A1AA] mb-2" size={48} /><p className="text-[#A1A1AA] text-sm">Selfie con documento en la barbilla</p></div>
                  )}
                  <input type="file" accept="image/*" capture="user" onChange={e => handleFileChange('selfie', e.target.files[0])} className="hidden" id="selfie-input" />
                  <div className="mt-2 w-full py-2.5 text-center rounded-lg bg-[#e8f5f0] border border-[#c5e4d9] text-sm font-medium text-[#2d7a5f] hover:bg-[#d4ede3] active:scale-[0.97] active:bg-[#c0e5d5] transition-all">{previews.selfie ? 'Cambiar foto' : 'Tomar / Subir foto'}</div>
                </label>
              </div>
              <div className="flex gap-3">
                <Button onClick={() => setStep(2)} variant="outline" className="flex-1">Atrás</Button>
                <Button onClick={handleSubmit} disabled={loading || !formData.selfie} className="flex-1 bg-[#00D4FF] text-black">
                  {loading ? 'Enviando...' : 'Enviar Verificación'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default KYC;
